<template>
  <div
    v-if="hasThreadSubmissions"
    data-component-group="business-form"
    data-component="enable-promotions"
  >
    <slot name="beforeForm" />

    <div v-if="!isDisabled" class="component-header">
      <v-autocomplete
        v-model="country"
        :items="threadSubmissions"
        prepend-inner-icon="public"
        :label="$t('country')"
        item-title="name"
        item-value="code"
        :return-object="true"
        required
        :error-messages="countryErrors"
        class="enable-promotions-country-input"
        @blur="v$.country.$touch()"
      />

      <div v-if="!isDisabled" class="component-actions">
        <v-btn
          color="secondary"
          :loading="isLoading"
          @click="onUpdateEnabledPromotions">
          <v-icon>edit</v-icon>
          {{ triggerLabel }}
        </v-btn>
      </div>
    </div>
    <div class="component-body">
      <Removables
        v-if="hasEnabledPromotions"
        :items="enabledPromotions"
        :is-disabled="isDisabled"
        :on-remove="onRemoveEnabledPromotion"
      >
        <template v-slot:item="scope" >
          <div class="d-flex align-center">
            <v-icon> public </v-icon>
            {{ scope.item.country.name }}
          </div>
        </template>
      </Removables>
    </div>
  </div>
</template>


<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import Removables from '../../lists/Removables';
  import { isStr } from '../../../utils';
  import { find } from 'lodash-es';

  export default {
    components: {
      Removables,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        country: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Enable',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        country: null,
        isLoading: false,
      };
    },
    computed: {
      userCountries() {
         return this.$store.getters['users/countries'];
      },
      threadSubmissions() {
        const { threadSubmissions } = this.$store.getters['businesses/activeItem'] || {};

        return threadSubmissions?.filter((item) => {
          return find(this.userCountries, { code: item.code });
        }) ?? [];

      },
      enabledPromotions() {
        return this.$store.getters['businesses/activeItem']?.enabledPromotions ?? [];
      },
      hasThreadSubmissions() {
        return this.threadSubmissions.length > 0;
      },
      hasEnabledPromotions() {
        return this.enabledPromotions.length > 0;
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('country_is_required');
          }
          return error.$message;
        });
      },
    },
    watch: {
      'business.id'(nextId, prevId) {
        if (nextId && prevId !== undefined) {
          this.clear();
        }
      },
      isDisabled(next) {
        if (next) {
          this.clear();
        }
      },
    },
    methods: {
      getEnabledPromotions({ id }) {
        const query = {
          id,
          only_allowed_countries: true,
        };
        return this.$store.dispatch('businesses/getEnabledPromotions', query);
      },
      onRemoveEnabledPromotion(item) {
        const query = {
          id: item.business_id,
          country: item.country.code,
        };

        return this.$store.dispatch('businesses/removeEnabledPromotion', query);
      },
      onUpdateEnabledPromotions() {
        const { v$, country, canSubmitRequest, onSubmit } = this;

        v$.$touch();

        if (!canSubmitRequest() || v$.$error || v$.pending) {
          return false;
        }
        this.isLoading = true;
        onSubmit(country)
        .then((res) => {
          this.clear();
          return res;
        })
        .catch(err=>err)
        .finally(()=>{
          this.isLoading = false;
        });
      },
      canSubmitRequest() {
        return isStr(this.country?.code);
      },
      clear() {
        this.v$.$reset();
        this.country = null;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-form'][data-component='enable-promotions'] {
  padding: $spacing-xl;
  background-color: map.get($colors, shades, 'white');
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .labels {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 10px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  [data-component='removables'] {
    padding: 16px 0 0 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: 62px;
      border-radius: $border-radius;
      background-color: map.get($colors, grey, lighten-3);
    }
  }
}
</style>
